import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import coaService from "../../services/coa";
import queryDefault from "../../utils/queryDefault";

type CoaFormValues = {
  code: string;
  description: string;
  type: string;
};

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function COA() {
  const notification = useNotification();
  const [keywordFilter, setKeywordFilter] = useState("");
  const [openFormModal, setOpenFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCode, setEditingCode] = useState("");
  const [isFetchingDetail, setIsFetchingDetail] = useState(false);

  const form = useForm<CoaFormValues>({
    defaultValues: {
      code: "",
      description: "",
      type: "",
    },
  });

  const coaList = useQuery(
    ["coa-list", keywordFilter],
    ({ queryKey }) => {
      const currentKeyword = String(queryKey[1] || "").trim();
      return coaService.listCOA({
        keyword: currentKeyword || undefined,
      });
    },
    {
      ...queryDefault,
    }
  );

  const rows = useMemo(() => get(coaList, "data.data", []), [coaList]);

  const createCOA = useMutation("create-coa", coaService.createCOA, {
    onSuccess: (response) => {
      notification.onOpen({
        message: response?.message || "COA was successfully created!",
        type: "success",
        position: "top",
      });
      setOpenFormModal(false);
      form.reset({
        code: "",
        description: "",
        type: "",
      });
      coaList.refetch();
    },
    onError: (error: any) => {
      notification.onOpen({
        message: error?.response?.data?.message || "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const updateCOA = useMutation(
    "update-coa",
    ({ code, payload }: { code: string; payload: { description: string; type: string } }) =>
      coaService.updateCOA(code, payload),
    {
      onSuccess: (response) => {
        notification.onOpen({
          message: response?.message || "COA was successfully updated!",
          type: "success",
          position: "top",
        });
        setOpenFormModal(false);
        setEditingCode("");
        setIsEditMode(false);
        form.reset({
          code: "",
          description: "",
          type: "",
        });
        coaList.refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const deleteCOA = useMutation("delete-coa", coaService.deleteCOA, {
    onSuccess: (response) => {
      notification.onOpen({
        message: response?.message || "COA was successfully deleted!",
        type: "success",
        position: "top",
      });
      coaList.refetch();
    },
    onError: (error: any) => {
      notification.onOpen({
        message: error?.response?.data?.message || "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleOpenCreate = () => {
    setIsEditMode(false);
    setEditingCode("");
    form.reset({
      code: "",
      description: "",
      type: "",
    });
    setOpenFormModal(true);
  };

  const handleOpenEdit = async (code: string) => {
    setIsFetchingDetail(true);
    try {
      const response = await coaService.getCOADetail(code);
      const data = get(response, "data", {});
      form.reset({
        code: String(get(data, "code", "")),
        description: String(get(data, "description", "")),
        type: String(get(data, "type", "")),
      });
      setEditingCode(code);
      setIsEditMode(true);
      setOpenFormModal(true);
    } catch (error: any) {
      notification.onOpen({
        message: error?.response?.data?.message || "Failed to load COA detail",
        type: "error",
        position: "top",
      });
    } finally {
      setIsFetchingDetail(false);
    }
  };

  const handleDelete = (code: string) => {
    const isConfirmed = window.confirm(`Delete COA ${code}?`);
    if (!isConfirmed) {
      return;
    }

    deleteCOA.mutate(code);
  };

  const onSubmit = (values: CoaFormValues) => {
    if (isEditMode) {
      updateCOA.mutate({
        code: editingCode,
        payload: {
          description: values.description,
          type: values.type,
        },
      });
      return;
    }

    createCOA.mutate({
      code: values.code,
      description: values.description,
      type: values.type,
    });
  };

  return (
    <MainLayout>
      <Breadcrumb label="COA Management" breadcrumbs={[{ label: "COA Management", href: "/app/coa" }]} />

      <ProtectedPermissionPage acceptPermissions={[1, 11]}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2} alignItems="end">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Keyword</FormLabel>
                <TextField
                  size="small"
                  value={keywordFilter}
                  onChange={(event) => setKeywordFilter(event.target.value)}
                  placeholder="Filter by keyword"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setKeywordFilter("");
                  }}
                >
                  Reset
                </Button>
                <Button variant="contained" onClick={handleOpenCreate}>
                  Create COA
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={coaList.isLoading || isFetchingDetail || deleteCOA.isLoading}
            getRowId={(record) => get(record, "code")}
            rows={rows}
            rowCount={rows.length}
            rowsPerPageOptions={[10, 20, 50]}
            pageSize={10}
            pagination
            disableSelectionOnClick
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "code",
                headerName: "Code",
                minWidth: 130,
                flex: 1,
                filterable: false,
              },
              {
                field: "description",
                headerName: "Description",
                minWidth: 240,
                flex: 1,
                filterable: false,
              },
              {
                field: "type",
                headerName: "Type",
                minWidth: 220,
                flex: 1,
                filterable: false,
              },
              {
                field: "is_active",
                headerName: "Status",
                minWidth: 120,
                flex: 1,
                filterable: false,
                renderCell: (params: any) => (
                  <Typography>{params.value ? "Active" : "Inactive"}</Typography>
                ),
              },
              {
                field: "actions",
                headerName: "Action",
                minWidth: 220,
                sortable: false,
                filterable: false,
                renderCell: (params: any) => {
                  const rowCode = String(get(params, "row.code", ""));
                  return (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenEdit(rowCode)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(rowCode)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>

      <Dialog open={openFormModal} onClose={() => setOpenFormModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? "Edit COA" : "Create COA"}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <FormLabel required>Code</FormLabel>
                  <Controller
                    name="code"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field: { ref, ...controllerField } }) => (
                      <TextField
                        {...controllerField}
                        size="small"
                        inputRef={ref}
                        disabled={isEditMode}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <FormLabel required>Description</FormLabel>
                  <Controller
                    name="description"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field: { ref, ...controllerField } }) => (
                      <TextField {...controllerField} size="small" inputRef={ref} />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <FormLabel required>Type</FormLabel>
                  <Controller
                    name="type"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field: { ref, ...controllerField } }) => (
                      <TextField {...controllerField} size="small" inputRef={ref} />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenFormModal(false);
              form.reset({
                code: "",
                description: "",
                type: "",
              });
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={form.handleSubmit(onSubmit)}
            loading={createCOA.isLoading || updateCOA.isLoading}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
